import type { PricingStrategyValues } from 'components/PricingStrategySection';
import { formatTimeForApi } from 'components/TimeInput/TimeInput';
import dayjs from 'dayjs';
import { isCustomizePackage, normalizeCustomPricePackageName } from 'utils/pricePackage';
import { isLineItemReadyForCosting } from 'screens/Authenticated/NewBooking/venueDetailsCostingUtils';

const unwrapDropdownValue = (value: unknown): unknown => {
  if (value != null && typeof value === 'object' && 'value' in (value as object)) {
    return (value as { value?: unknown }).value;
  }
  return value;
};

/** Map edit-booking form state into the shape expected by {@link isLineItemReadyForCosting}. */
export const buildEditBookingCostingDraft = (
  updatedData: Record<string, any>,
  status: string | undefined,
): Record<string, any> => ({
  ...updatedData,
  guests: updatedData.guaranteedGuests,
  status: status ?? updatedData.status,
  venue: unwrapDropdownValue(updatedData.venue) ?? updatedData.venue,
  partOfDay: unwrapDropdownValue(updatedData.partOfDay) ?? updatedData.partOfDay,
  pricePackage: unwrapDropdownValue(updatedData.pricePackage) ?? updatedData.pricePackage,
});

export const isEditBookingReadyForCosting = (
  updatedData: Record<string, any>,
  status: string | undefined,
  hourlyPricing: boolean,
): boolean => isLineItemReadyForCosting(buildEditBookingCostingDraft(updatedData, status), hourlyPricing);

type BuildEditBookingCostingRequestArgs = {
  updatedData: Record<string, any>;
  masterDataObject: Record<string, any>;
  pricing: PricingStrategyValues;
  bookedAddOns: any[];
  hourlyPricing: boolean;
  pricePackages: { value: any; label: string }[];
  isComplementary: boolean;
  /** Current line item id — excluded from venue overlap checks on the server. */
  excludeBookingLineItemId?: number | string | null;
};

/** Builds POST `/new-booking/costing` body from the edit-booking draft. */
export const buildEditBookingCostingRequest = ({
  updatedData,
  masterDataObject,
  pricing,
  bookedAddOns,
  hourlyPricing,
  pricePackages,
  isComplementary,
  excludeBookingLineItemId,
}: BuildEditBookingCostingRequestArgs): Record<string, any> | null => {
  if (!isEditBookingReadyForCosting(updatedData, masterDataObject?.status, hourlyPricing)) {
    return null;
  }

  const startDate = dayjs(updatedData.startDate);
  const endDate = dayjs(updatedData.endDate);
  if (!startDate.isValid() || !endDate.isValid()) return null;

  const formattedStart = startDate.format('DD-MM-YYYY');
  const formattedEnd = endDate.format('DD-MM-YYYY');
  const venueId = unwrapDropdownValue(updatedData.venue);
  const eventTypeId = unwrapDropdownValue(updatedData.eventType);
  const partOfDay = unwrapDropdownValue(updatedData.partOfDay);
  const pricePackageId = unwrapDropdownValue(updatedData.pricePackage);
  const layoutId = unwrapDropdownValue(updatedData.layout);
  const customerName = String(masterDataObject?.customerName ?? '').trim();
  const [firstName, ...restName] = customerName.split(/\s+/).filter(Boolean);

  const serviceChargePercentage =
    updatedData.serviceCharge != null && typeof updatedData.serviceCharge === 'object'
      ? (updatedData.serviceCharge as { value?: unknown }).value
      : updatedData.serviceChargePercentage ?? updatedData.serviceCharge;

  const toPercent = (value: unknown): number | null => {
    if (value === '' || value == null) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const toDays = (value: unknown): number => {
    if (value === '' || value == null) return 0;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const excludeId =
    excludeBookingLineItemId != null && excludeBookingLineItemId !== ''
      ? Number(excludeBookingLineItemId)
      : null;

  return {
    firstName: firstName || 'Guest',
    lastName: restName.join(' ') || undefined,
    email: masterDataObject?.email || undefined,
    startDate: formattedStart,
    endDate: formattedEnd,
    eventType: eventTypeId,
    complementary: isComplementary,
    ...(excludeId != null && Number.isFinite(excludeId)
      ? { excludeBookingLineItemId: excludeId }
      : {}),
    holdDays: Number(updatedData.holdDays) || 0,
    bookingConfirmationBy: updatedData.bookingConfirmationBy,
    additionalComments: updatedData.additionalComments,
    // Required by BookingRequestValidator (percentages must total 100).
    minConfirmPaymentPercentage: toPercent(updatedData.minConfirmPaymentPercentage),
    paymentSlotOnePercentage: toPercent(updatedData.paymentSlotOnePercentage),
    paymentSlotTwoPercentage: toPercent(updatedData.paymentSlotTwoPercentage),
    paymentSlotThreePercentage: toPercent(updatedData.paymentSlotThreePercentage),
    paymentSlotOneBeforeDays: toDays(updatedData.paymentSlotOneBeforeDays),
    paymentSlotTwoBeforeDays: toDays(updatedData.paymentSlotTwoBeforeDays),
    fullPaymentBeforeDays: toDays(updatedData.fullPaymentBeforeDays),
    bookedRooms: [],
    externalBookedRooms: [],
    venues: [
      {
        venue: venueId,
        eventSubtype:
          typeof updatedData.eventSubtype === 'object'
            ? (updatedData.eventSubtype as any)?.label || (updatedData.eventSubtype as any)?.value || ''
            : updatedData.eventSubtype,
        placeOfEvent: updatedData.placeOfEvent,
        addressOfEvent: updatedData.addressOfEvent,
        guests: Number(updatedData.guaranteedGuests),
        excessGuestsPercentage:
          updatedData.excessGuestsPercentage === '' || updatedData.excessGuestsPercentage == null
            ? null
            : Number(updatedData.excessGuestsPercentage),
        startDate: formattedStart,
        endDate: formattedEnd,
        startTime: formatTimeForApi(updatedData.startTime as string),
        endTime: formatTimeForApi(updatedData.endTime as string),
        partOfDay,
        status: masterDataObject?.status,
        pricePackageId: hourlyPricing ? null : pricePackageId,
        customPricePackageName:
          !hourlyPricing && isCustomizePackage(pricePackageId, pricePackages)
            ? normalizeCustomPricePackageName(updatedData.customPricePackageName)
            : null,
        layout: layoutId || '',
        preLeadTime:
          updatedData.preLeadTime === '' || updatedData.preLeadTime == null
            ? null
            : Number(updatedData.preLeadTime),
        postLeadTime:
          updatedData.postLeadTime === '' || updatedData.postLeadTime == null
            ? null
            : Number(updatedData.postLeadTime),
        taxGroupOnGuestChargeId:
          unwrapDropdownValue(updatedData.taxGroupOnGuestCharge) ?? updatedData.taxGroupOnGuestCharge ?? null,
        taxGroupOnHallRentalId:
          unwrapDropdownValue(updatedData.taxGroupOnHallRental) ?? updatedData.taxGroupOnHallRental ?? null,
        taxGroupOnServiceChargeId:
          unwrapDropdownValue(updatedData.taxGroupOnServiceCharge) ?? updatedData.taxGroupOnServiceCharge ?? null,
        serviceChargePercentage,
        pricePerPerson: pricing.overridePricePerPerson ? pricing.pricePerPerson : null,
        minimumRevenue: pricing.overrideMinimumRevenue ? pricing.minimumRevenue : null,
        hallRental: pricing.overrideHallRental ? pricing.hallRental : null,
        pricePerHour: hourlyPricing && pricing.overridePricePerHour ? pricing.pricePerHour : null,
        overridePricePerPerson: pricing.overridePricePerPerson,
        overrideMinimumRevenue: pricing.overrideMinimumRevenue,
        overrideHallRental: pricing.overrideHallRental,
        overridePricePerHour: pricing.overridePricePerHour,
        addOns: bookedAddOns,
      },
    ],
  };
};
